import { baseApi } from './baseApi';

// Extended merchant config with additional properties needed for the merchant API
export interface MerchantGatewayConfig {
  id?: string;
  merchantId: string;
  fundCode?: string; // Optional field for fund code, used in RTA context
  checksumKey?: string;
  amc: { id: string };
  aggregator: 'Billdesk1' | 'Billdesk2' | 'CamsPay' | 'RazorPay' | 'CashFree' | 'ePay';
  bankId?: string;
  clientId?: string;
}

// Payment aggregator types
export interface Aggregator {
  value: string;
  label: string;
  displayName: string;
}

// Backend API response format
export interface GetGatewaysApiResponse {
  gateways: string[];
}

export interface GetAggregatorsResponse {
  aggregators: Aggregator[];
}

export const merchantApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getMerchantConfigs: builder.query<MerchantGatewayConfig[], void>({
      query: () => '/merchant-configs',
      providesTags: ['MerchantConfig'],
    }),

    getMerchantConfigsByAMC: builder.query<MerchantGatewayConfig[], string>({
      query: (amcId) => `/merchant-configs/amc/${amcId}`,
      providesTags: ['MerchantConfig'],
    }),

    createMerchantConfig: builder.mutation<
      MerchantGatewayConfig,
      Omit<MerchantGatewayConfig, 'id'>
    >({
      query: (config) => ({
        url: '/merchant-configs',
        method: 'POST',
        body: config,
      }),
      invalidatesTags: ['MerchantConfig'],
    }),

    updateMerchantConfig: builder.mutation<
      MerchantGatewayConfig,
      Partial<MerchantGatewayConfig> & Pick<MerchantGatewayConfig, 'id'>
    >({
      query: ({ id, ...patch }) => ({
        url: `/merchant-configs/${id}`,
        method: 'PUT',
        body: patch,
      }),
      invalidatesTags: (_result, _err, { id }) => [
        { type: 'MerchantConfig', id },
        'MerchantConfig',
      ],
    }),

    deleteMerchantConfig: builder.mutation<void, string>({
      query: (id) => ({
        url: `/merchant-configs/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _err, id) => [{ type: 'MerchantConfig', id }],
    }),

    // Gateway exclusions
    getGatewayExclusions: builder.query<
      { exclusions: string[] },
      { assetId?: string; amcId?: string }
    >({
      query: ({ assetId, amcId }) => {
        const params = new URLSearchParams();
        if (assetId) params.set('assetId', assetId);
        if (amcId) params.set('amcId', amcId);
        const qs = params.toString();
        return `/merchant-configs/exclusions${qs ? `?${qs}` : ''}`;
      },
      providesTags: ['GatewayExclusion'],
    }),

    createGatewayExclusion: builder.mutation<
      void,
      { gatewayId: string; ttlMs: number; assetId?: string; amcId?: string }
    >({
      query: (body) => ({
        url: '/merchant-configs/exclusions',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['GatewayExclusion'],
    }),

    deleteGatewayExclusion: builder.mutation<
      void,
      { gatewayId: string; assetId?: string; amcId?: string }
    >({
      query: ({ gatewayId, assetId, amcId }) => {
        const params = new URLSearchParams();
        if (assetId) params.set('assetId', assetId);
        if (amcId) params.set('amcId', amcId);
        const qs = params.toString();
        return {
          url: `/merchant-configs/exclusions/${gatewayId}${qs ? `?${qs}` : ''}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: ['GatewayExclusion'],
    }),

    // Get all available aggregators from backend enum (for Add Payment Aggregator form)
    getAllAggregators: builder.query<GetAggregatorsResponse, void>({
      query: () => '/merchant-configs/aggregators',
      transformResponse: (response: GetGatewaysApiResponse): GetAggregatorsResponse => {
        // Transform backend response to UI format
        const aggregators = response.gateways.map((gateway) => {
          // Use gateway name as-is from backend
          const normalizedValue = gateway;

          return {
            value: normalizedValue,
            label: normalizedValue,
            displayName: normalizedValue,
          };
        });

        return { aggregators };
      },
      providesTags: ['Aggregator'],
    }),

    // Get configured aggregators for an AMC (for filtering in Dashboard/Reports)
    getAggregators: builder.query<GetAggregatorsResponse, string | undefined>({
      query: (amcId) => {
        const params = new URLSearchParams();
        if (amcId) params.set('amcId', amcId);
        const qs = params.toString();
        return `/dashboard/gateways${qs ? `?${qs}` : ''}`;
      },
      transformResponse: (response: { gateways: string[] }): GetAggregatorsResponse => {
        // Transform backend response to UI format
        const aggregators = response.gateways.map((gateway) => {
          // Use gateway name as-is from backend
          const normalizedValue = gateway;

          return {
            value: normalizedValue,
            label: normalizedValue,
            displayName: normalizedValue,
          };
        });

        return { aggregators };
      },
      providesTags: ['Aggregator'],
    }),

    getMerchantConfigLogs: builder.query<any, string>({
      query: (configId) => `/merchant-configs/logs/${configId}`,
    }),
  }),
});

export const {
  useGetMerchantConfigsQuery,
  useGetMerchantConfigsByAMCQuery,
  useCreateMerchantConfigMutation,
  useUpdateMerchantConfigMutation,
  useDeleteMerchantConfigMutation,
  useGetGatewayExclusionsQuery,
  useCreateGatewayExclusionMutation,
  useDeleteGatewayExclusionMutation,
  useGetAllAggregatorsQuery,
  useGetAggregatorsQuery,
  useGetMerchantConfigLogsQuery,
} = merchantApi;
